import { Component, computed, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { DashboardItem } from '../../../api/app-types/dashboard.app';
import { CLIENT_API } from '../../../api/injection-tokens/services.token';
import { LvrHeading } from '../../../core/components/heading/heading';
import { Paginator } from '../../../core/components/paginator/paginator';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { SpecialistCard } from '../specialist-card/specialist-card';

@Component({
	selector: 'lvr-specialists-overview',
	imports: [TranslocoPipe, MatIcon, SpecialistCard, Paginator, LvrHeading],
	templateUrl: './specialists-overview.html',
	styleUrl: './specialists-overview.scss',
})
export class SpecialistsOverview extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(CLIENT_API);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly representationMode = inject(REPRESENTATION_MODE);

	private readonly lastRepresentationKey = signal<string | null>(null);
	private readonly representationKey = computed(() => {
		const isActive = this.representationMode.isActive();
		return isActive ? (this.representationMode.mode().representedUserId ?? '') : '';
	});
	private readonly currentPageSize = computed(
		() => this.linkedPage().range || this.page().range || this.env.defaultPageSize,
	);

	public readonly page = input.required<PaginatedResponse<DashboardItem>>();

	private readonly initialPage = computed<PaginatedResponse<DashboardItem>>(() => {
		const pagination = this.page();
		return {
			entities: pagination.entities,
			offset: pagination.offset,
			range: pagination.range ?? this.env.defaultPageSize,
			totalEntities: pagination.totalEntities,
		};
	});

	public readonly linkedPage = linkedSignal({
		source: this.initialPage,
		computation: (init) => ({
			...init,
			range: init.range || this.env.defaultPageSize,
		}),
	});

	public readonly loading = signal(false);
	public readonly showFirstLastButtons = true;
	public readonly hidePageSize = false;

	public readonly users = computed(() => this.linkedPage().entities);
	public readonly totalItems = computed(() => this.linkedPage().totalEntities);
	public readonly pageIndex = computed(() => this.page()?.offset ?? 0);
	public readonly pageSize = computed(() => this.page()?.range ?? this.env.defaultPageSize);

	public constructor() {
		super();
		effect(() => {
			const key = this.representationKey();
			const pageSize = this.currentPageSize();

			if (this.lastRepresentationKey() === null) {
				this.lastRepresentationKey.set(key);

				if (key) {
					void this.reloadPage(0, pageSize);
				}
				return;
			}

			if (this.lastRepresentationKey() === key) return;

			this.lastRepresentationKey.set(key);
			void this.reloadPage(0, pageSize);
		});
	}

	public handlePageEvent(event: PageEvent) {
		this.reloadPage(event.pageIndex, event.pageSize);
	}

	private async reloadPage(pageIndex: number, pageSize: number) {
		this.loading.set(true);
		try {
			const userId = this.representationMode.isActive() ? this.representationMode.mode().representedUserId : undefined;
			const update = await this.api.getAllDashboard(pageIndex, pageSize, userId);
			this.linkedPage.set({ ...update, offset: pageIndex, range: pageSize });
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.dashboard_users_reloading_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}
}
